import { useState, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { tryParseToTiptapHTML } from '@tiptap-pro/extension-ai';
import { DOMSerializer } from 'prosemirror-model';
import { t } from 'i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { bloxColors } from 'themes/constants';
import SelectDropdown from '../../SelectDropdown/SelectDropdown';
import { CustomToolbarBtnProps } from '../types/customToolbarBtnTypes';
import EditorWithBar from 'components/EditorWithBar/EditorWithBar';
import { DialogTitle } from 'components/WizardWrapper/components/DialogTitle';
import {
  dialogActionsSx,
  dialogActionContainerSx,
  dialogActionButtonSx,
} from 'components/WizardWrapper/components/DialogActions';
import { getDialogContentStyle } from 'components/FormControls/InputSelectDialog/InputSelectDialogStyles';
import { createUncontrolledEditor } from 'components/RichTextEditor/hooks/useCreateEditor';
import { hasExtension } from 'components/EditorBar/utils';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { debounce } from 'utility/functions';
import { useAppSettings } from 'providers/contexts/SettingsContext';

export default function AISelect({ editor, disabled, sx }: CustomToolbarBtnProps) {
  const {
    appSettings: { site: siteSettings },
  } = useAppSettings();
  const { sessionConfig } = useCosmos();
  const hasAiSelectExt = hasExtension(editor, 'ai');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const responseEditorRef = useRef<Editor | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState<string | null>(null);
  const [translatedCommand, setTranslatedCommand] = useState<string | null>(null);
  const [originalText, setOriginalText] = useState<string | null>(null);
  const [isContentEmpty, setIsContentEmpty] = useState<boolean>(true);
  const [editorKey, setEditorKey] = useState(0);
  const [aiStorage, setAiStorage] = useState<any | undefined>(undefined);

  // Sync editor storage with a react state for proper rerendering
  useEffect(() => {
    if (!editor) return;

    const updateAiStorage = debounce(() => {
      setAiStorage((prev: any) => ({
        ...prev,
        ...(editor?.storage?.ai || editor?.storage?.aiAdvanced),
      }));
    }, 100);

    editor.on('create', updateAiStorage);
    editor.on('transaction', updateAiStorage);

    updateAiStorage();

    return () => {
      editor.off('create', updateAiStorage);
      editor.off('transaction', updateAiStorage);
    };
  }, [editor]);

  // Create the response editor when AI response is ready
  useEffect(() => {
    if (isDialogOpen && aiStorage?.state === 'idle' && aiStorage?.response && editor) {
      // Destroy existing response editor if any
      if (responseEditorRef.current) {
        responseEditorRef.current?.destroy();
      }

      // Parse AI response and create a new response editor
      const parsedResponse = tryParseToTiptapHTML(aiStorage?.response, editor) || '';

      const newEditor = createUncontrolledEditor({
        sessionConfig: sessionConfig,
        editorConfig: {
          contentForEditor: parsedResponse,
          id: 'response',
          type: 'richtext',
          showCount: 'word',
          placeholder: 'Response',
          disableDropCursor: true,
        },
      });

      responseEditorRef.current = newEditor;
      setEditorKey((prev) => prev + 1);

      // Init content empty state
      setIsContentEmpty(newEditor?.isEmpty);

      // Listen for content updates to manage the isContentEmpty state
      const handleUpdate = debounce(() => {
        setIsContentEmpty(newEditor.isEmpty);
      }, 100);

      newEditor.on('create', handleUpdate);
      newEditor.on('transaction', handleUpdate);
      return () => {
        newEditor.on('create', handleUpdate);
        newEditor.off('transaction', handleUpdate);
      };
    }
  }, [isDialogOpen, aiStorage, sessionConfig, editor]);

  // Update translatedCommand whenever selectedCommand changes
  useEffect(() => {
    if (selectedCommand) {
      const translation = t(
        `blox.assistant.content.actions.${selectedCommand.toLowerCase()}`,
      );
      setTranslatedCommand(translation);
    } else {
      setTranslatedCommand('');
    }
  }, [selectedCommand]);

  // Open the dialog and initiate the AI command
  const handleOpenDialog = (command: string, aiFunction: () => void) => {
    if (!editor) return;

    setSelectedCommand(command);

    // Extract the HTML for the selection
    const from = editor?.state?.selection?.from;
    const to = editor?.state?.selection?.to;
    const slice = editor?.state?.doc?.slice(from, to);
    // ProseMirror's DOMSerializer
    const serializer = DOMSerializer.fromSchema(editor?.schema);
    const container = document.createElement('div');

    // Serialize each node in the slice to HTML
    slice.content.forEach((node) => {
      const domNode = serializer.serializeNode(node);
      container.appendChild(domNode);
    });

    // Extract the resulting HTML
    const selectedHtml = container.innerHTML;
    // Set the HTML content for original display
    setOriginalText(selectedHtml || '(No content selected)');
    setIsDialogOpen(true);

    // Run the AI command for the selected option
    aiFunction();
  };

  // Close the dialog and clean up
  const handleCloseDialog = () => {
    setSelectedCommand(null);
    setOriginalText(null);
    setIsDialogOpen(false);

    // Destroy the response editor instance
    if (responseEditorRef.current) {
      responseEditorRef.current?.destroy();
      responseEditorRef.current = null;
    }
    setEditorKey((prev) => prev + 1);
  };

  // Change command selection
  const handleCommandChange = (command: string) => {
    const originalKey = Object.keys(optionFunctions).find(
      (key) => t(`blox.assistant.content.actions.${key.toLowerCase()}`) === command,
    ) as keyof typeof optionFunctions;

    setSelectedCommand(originalKey);
    const aiFunction = optionFunctions[originalKey];
    if (aiFunction) aiFunction();
  };

  // Regenerate the AI suggestion
  const handleRegenerate = () => {
    const aiFunction = optionFunctions[selectedCommand as keyof typeof optionFunctions];
    if (aiFunction) aiFunction();
  };

  // Accept the AI suggestion and insert it into the main editor
  const handleAccept = () => {
    if (editor && responseEditorRef.current) {
      // Get HTML content from the response editor
      const rawContent = responseEditorRef.current?.getHTML()?.trim();
      const parser = new DOMParser();
      const parsedContent = parser.parseFromString(rawContent, 'text/html');
      const body = parsedContent?.body;

      // Process content
      const nodes = Array.from(body?.childNodes);
      const cleanedContent = nodes
        .map((node, index) => {
          if (node.nodeName === 'P' && node instanceof Element) {
            return index === 0 ? node.innerHTML : node.outerHTML;
          }
          return node instanceof Element ? node.outerHTML : node.textContent || '';
        })
        .join('');

      const from = editor?.state?.selection?.from;
      const to = editor?.state.selection.to;
      editor
        ?.chain()
        ?.focus()
        ?.insertContentAt({ from, to }, cleanedContent, {
          parseOptions: { preserveWhitespace: true },
        })
        ?.run();
    }
    handleCloseDialog();
  };

  // Reject the AI suggestion
  const handleReject = () => {
    editor?.chain()?.focus()?.aiReject()?.run();
    handleCloseDialog();
  };

  // Handle option selection
  const handleOptionSelect = (index: number) => {
    // Check if the editor is in bubble menu mode and close it
    if (editor && editor.storage.bubbleMenu) {
      editor.storage.bubbleMenu = { view: false };
    }

    const selectedKey = Object.keys(optionFunctions)[
      index
    ] as keyof typeof optionFunctions;
    optionFunctions[selectedKey]?.();
  };

  // Define AI command options and their corresponding functions
  const optionFunctions = {
    Complete: () =>
      handleOpenDialog('Complete', () =>
        editor?.chain()?.focus()?.aiComplete({ insert: false })?.run(),
      ),
    Shorten: () =>
      handleOpenDialog('Shorten', () =>
        editor?.chain()?.focus()?.aiShorten({ insert: false })?.run(),
      ),
    Extend: () =>
      handleOpenDialog('Extend', () =>
        editor?.chain()?.focus()?.aiExtend({ insert: false })?.run(),
      ),
    Rephrase: () =>
      handleOpenDialog('Rephrase', () =>
        editor?.chain()?.focus()?.aiRephrase({ insert: false })?.run(),
      ),
    Simplify: () =>
      handleOpenDialog('Simplify', () =>
        editor?.chain()?.focus()?.aiSimplify({ insert: false })?.run(),
      ),
    Emojify: () =>
      handleOpenDialog('Emojify', () =>
        editor?.chain()?.focus()?.aiEmojify({ insert: false })?.run(),
      ),
  };

  const options = Object.keys(optionFunctions).map((key) =>
    t(`blox.assistant.content.actions.${key.toLowerCase()}`),
  );

  return (
    <>
      <SelectDropdown
        options={options}
        onOptionSelect={handleOptionSelect}
        disabled={
          !hasAiSelectExt ||
          disabled ||
          editor?.view?.state?.selection?.empty ||
          !siteSettings?.editorial?.content_extensions
        }
        placeholder={<AutoAwesomeOutlinedIcon />}
        tooltip={t('blox.assistant.content.label')}
        sx={sx}
        data-testid="ai-select-dropdown"
      />
      {aiStorage && (
        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          slotProps={{
            paper: {
              sx: isMobile ? undefined : { height: '90vh' },
            },
          }}
        >
          <DialogTitle
            icon={<AutoAwesomeOutlinedIcon />}
            title={t('blox.assistant.content.label')}
            onClose={handleCloseDialog}
          />
          <DialogContent sx={{ ...getDialogContentStyle(theme) }}>
            <Box sx={{ pt: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: isDarkMode ? '#1E1E1E' : bloxColors.white,
                  pb: theme.spacing(2),
                }}
              >
                <FormControl
                  fullWidth
                  variant="outlined"
                  disabled={!hasAiSelectExt || aiStorage?.state === 'loading'}
                >
                  <Select
                    value={translatedCommand || ''}
                    onChange={(event) => handleCommandChange(event?.target?.value)}
                    inputProps={{
                      'aria-label': t('blox.assistant.content.actions.select'),
                    }}
                  >
                    {options.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip
                  title={t('blox.actions.retry')}
                  disableInteractive
                >
                  <span>
                    <IconButton
                      onClick={handleRegenerate}
                      disabled={
                        !hasAiSelectExt ||
                        !editor?.can()?.aiRegenerate() ||
                        aiStorage?.state === 'loading'
                      }
                      color="success"
                    >
                      {aiStorage?.state === 'loading' ? (
                        <CircularProgress
                          size={27.42}
                          color="info"
                        />
                      ) : (
                        <AutorenewIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle1">
                  {t('blox.general.suggestion')}
                </Typography>
              </Box>
              {aiStorage?.state === 'loading' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '150px',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {aiStorage?.state === 'idle' && responseEditorRef.current && (
                <EditorWithBar
                  key={editorKey}
                  editor={responseEditorRef.current}
                  id="response"
                  type="richtext"
                  showCount="word"
                />
              )}
              <Divider sx={{ mt: 3, mb: 3 }} />
              <Typography
                variant="subtitle1"
                sx={{ mb: 1 }}
              >
                {t('blox.general.original')}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  maxHeight: '200px',
                  p: 1,
                  '& p:first-of-type': { pt: 0 },
                  '& p:last-of-type': { pb: 0 },
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.background.paper,
                  color: theme.palette.text.secondary,
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{
                  __html: originalText || '',
                }}
              />
            </Box>
            {aiStorage?.state === 'error' && (
              <Box
                sx={{
                  color: 'error.main',
                }}
              >
                Error: {aiStorage?.error?.message}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={dialogActionsSx(theme)}>
            <Box sx={dialogActionContainerSx(theme)}>
              <Button
                onClick={handleRegenerate}
                disabled={
                  !hasAiSelectExt ||
                  !hasAiSelectExt ||
                  !editor?.can()?.aiRegenerate() ||
                  aiStorage?.state === 'loading'
                }
                color="success"
                variant="outlined"
                sx={dialogActionButtonSx(theme, isMobile)}
              >
                {aiStorage?.state === 'loading' ? (
                  <CircularProgress
                    size={22.84}
                    color="info"
                    sx={{ p: 0.5 }}
                  />
                ) : (
                  <AutorenewIcon fontSize="small" />
                )}
                {t('blox.actions.retry')}
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                onClick={handleAccept}
                disabled={!hasAiSelectExt || !editor?.can()?.aiAccept() || isContentEmpty}
                variant="contained"
                sx={dialogActionButtonSx(theme, isMobile)}
              >
                {t('blox.actions.apply')}
              </Button>
              <Button
                onClick={handleReject}
                disabled={!hasAiSelectExt || !editor?.can()?.aiReject()}
                variant="outlined"
                sx={dialogActionButtonSx(theme, isMobile)}
              >
                {t('blox.actions.cancel')}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
